import React, { useState } from "react";
import {
  Paper,
  Stack,
  Typography,
  Box,
  Chip,
  Skeleton,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";

import InfoButton from "../Clinical/InfoButton";
import CMStatCards from "./CMStatCards";
import OutreachTable from "./OutreachTable";

/**
 * Panel de Outreach & Referrals — muestra stats de contacto,
 * filtros por canal y tabla de pacientes pendientes.
 *
 * @param {{ data: Object, loading: boolean, onCreateTask: Function, helpContent: Object }} props
 */
export default function PanelOutreach({ data, loading, onCreateTask, helpContent }) {
  const [filter, setFilter] = useState("all");

  // ─── Loading state ─────────────────────────────────────────────
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={2}>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} animation="wave" />
        </Stack>
      </Paper>
    );
  }

  // ─── Empty state ───────────────────────────────────────────────
  if (!data?.patients?.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={2}>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: 1.5,
                bgcolor: "#DCFCE7",
                color: "#16A34A",
              }}
            >
              <PhoneIcon sx={{ fontSize: 16 }} />
            </Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Outreach & Referrals
            </Typography>
            <InfoButton helpContent={helpContent} />
          </Stack>

          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            All patients contacted recently
          </Typography>
        </Stack>
      </Paper>
    );
  }

  // ─── Filter chip definitions ───────────────────────────────────
  const filterChips = [
    { key: "all", label: "All" },
    { key: "phone", label: "With Phone Only" },
    { key: "email", label: "With Email Only" },
  ];

  // ─── Stat cards items ─────────────────────────────────────────
  const statItems = [
    {
      label: "Patients to Contact",
      value: data.stats?.patientsToContact,
      color: "#16A34A",
      bgColor: "#DCFCE7",
    },
    {
      label: "With Phone",
      value: data.stats?.withPhone,
      color: "#2563EB",
      bgColor: "#EFF6FF",
    },
    {
      label: "With Email",
      value: data.stats?.withEmail,
      color: "#0891B2",
      bgColor: "#ECFEFF",
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: 1.5,
              bgcolor: "#DCFCE7",
              color: "#16A34A",
            }}
          >
            <PhoneIcon sx={{ fontSize: 16 }} />
          </Box>
          <Typography variant="subtitle2" fontWeight={700}>
            Outreach & Referrals
          </Typography>
          <InfoButton helpContent={helpContent} />
        </Stack>

        {/* Stat Cards */}
        <CMStatCards items={statItems} />

        {/* Filter Chips */}
        <Stack direction="row" spacing={1}>
          {filterChips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              size="small"
              variant={filter === chip.key ? "filled" : "outlined"}
              color={filter === chip.key ? "primary" : "default"}
              onClick={() => setFilter(chip.key)}
              sx={{ fontWeight: filter === chip.key ? 600 : 400 }}
            />
          ))}
        </Stack>

        {/* Table */}
        <OutreachTable
          patients={data.patients}
          filter={filter}
          onCreateTask={onCreateTask}
        />
      </Stack>
    </Paper>
  );
}
